import { space, colors } from './core'
import { textStyles } from './typography'
import { muiColors } from '../mui/DefaultTheme'

const baseRadios = {
  cursor: 'pointer',
  borderColor: 'neutrals.3',
  borderStyle: 'solid',
  borderWidth: '1px',
  color: 'neutrals.4',
  '@media (hover: hover)': { // Only apply these styles if the browser supports proper/true/real/non-emulated hovering (e.g. has a mouse-like primary input device)
    '&:hover': {
      borderColor: 'secondary.2',
      color: 'secondary.2',
      backgroundColor: `${colors.secondary[2]}20`
    }
  }
}

const baseRadiosProminentColourScheme = {
  cursor: 'pointer',
  backgroundColor: muiColors.primary.white,
  color: muiColors.prominent.darkGrey(0.8),
  outline: `solid 1px ${muiColors.prominent.lightGrey()}`,
  '@media (hover: hover)': { // Only apply these styles if the browser supports proper/true/real/non-emulated hovering (e.g. has a mouse-like primary input device)
    '&:hover': {
      color: muiColors.prominent.darkGrey(),
      backgroundColor: muiColors.prominent.blue(0.09),
      outline: 'none'
    }
  }
}

const baseSelectedRadios = {
  borderColor: 'secondary.1',
  borderWidth: '2px',
  color: 'neutrals.0',
  backgroundColor: 'secondary.1'
}

const baseSelectedRadiosProminentColourScheme = {
  color: muiColors.prominent.blue(),
  backgroundColor: muiColors.prominent.blue(0.09),
  border: `solid 2px ${muiColors.prominent.blue()}`
}

const baseFocusRadios = {
  outlineWidth: '2px',
  outlineStyle: 'solid',
  outlineColor: 'secondary.1',
  outlineOffset: '1px',
  boxShadow: `0px 5px 4px 0px ${colors.secondary[2]}40`,
  '&:hover': {
    outlineColor: 'secondary.2'
  }
}

const radios = {
  doubleColumn: {
    ...baseRadios,
    ...textStyles.body.regular.regular,
    borderRadius: '4px',
    'input[type="radio"]:checked + &.doubleColumn': { // Need to keep this last class because of Styled Components and static classes
      p: `calc(${space[3]}px - 1px)`,
      ...baseSelectedRadios
    },
    'input[type="radio"]:focus + &.doubleColumn': { // Need to keep this last class because of Styled Components and static classes
      ...baseFocusRadios
    },
    p: 3,
    my: 3,
    flexBasis: 'calc(50% - 34px)',
    display: 'grid',
    alignItems: 'center'
  },
  doubleColumnProminentColourScheme: {
    ...baseRadiosProminentColourScheme,
    ...textStyles.body.regular.regular,
    borderRadius: '4px',
    'input[type="radio"]:checked + &.doubleColumnProminentColourScheme': { // Need to keep this last class because of Styled Components and static classes
      p: `calc(${space[3]}px - 1px)`,
      ...baseSelectedRadiosProminentColourScheme
    },
    'input[type="radio"]:focus + &.doubleColumnProminentColourScheme': { // Need to keep this last class because of Styled Components and static classes
      ...baseFocusRadios
    },
    p: 3,
    my: 3,
    flexBasis: 'calc(50% - 34px)',
    display: 'grid',
    alignItems: 'center'
  },

  standard: {
    ...baseRadios,
    ...textStyles.body.regular.regular,
    borderRadius: '4px',
    'input[type="radio"]:checked + &.standard': { // Need to keep this last class because of Styled Components and static classes
      p: `calc(${space[3]}px - 1px)`,
      ...baseSelectedRadios
    },
    'input[type="radio"]:focus + &.standard': { // Need to keep this last class because of Styled Components and static classes
      ...baseFocusRadios
    },
    p: 3,
    my: 3,
    flexGrow: '1'
  },
  standardProminentColourScheme: {
    ...baseRadiosProminentColourScheme,
    ...textStyles.body.regular.regular,
    borderRadius: '4px',
    'input[type="radio"]:checked + &.standardProminentColourScheme': { // Need to keep this last class because of Styled Components and static classes
      p: `calc(${space[3]}px - 1px)`,
      ...baseSelectedRadiosProminentColourScheme
    },
    'input[type="radio"]:focus + &.standardProminentColourScheme': { // Need to keep this last class because of Styled Components and static classes
      ...baseFocusRadios
    },
    p: 3,
    my: 3,
    flexGrow: '1'
  },

  small: {
    ...baseRadios,
    ...textStyles.body.smallPrint.medium,
    justifyContent: 'center',
    borderRadius: '2px',
    'input[type="radio"]:checked + &.small': { // Need to keep this last class because of Styled Components and static classes
      p: `calc(${space[2]}px - 1px)`,
      ...baseSelectedRadios
    },
    'input[type="radio"]:focus + &.small': { // Need to keep this last class because of Styled Components and static classes
      ...baseFocusRadios
    },
    p: 2,
    my: 3,
    flexGrow: '1'
  },

  experimental: {
    ...baseRadios,
    ...textStyles.body.regular.regular,
    borderRadius: '4px',
    'input[type="radio"]:checked + &.standard': { // Need to keep this last class because of Styled Components and static classes
      p: `calc(${space[3]}px - 1px)`,
      ...baseSelectedRadios
    },
    'input[type="radio"]:focus + &.standard': { // Need to keep this last class because of Styled Components and static classes
      ...baseFocusRadios
    },
    p: 3,
    mt: '2px',
    mb: 2,
    flexGrow: '1'
  },
  experimentalProminentColourScheme: {
    ...baseRadiosProminentColourScheme,
    ...textStyles.body.regular.regular,
    borderRadius: '4px',
    'input[type="radio"]:checked + &.standardProminentColourScheme': { // Need to keep this last class because of Styled Components and static classes
      p: `calc(${space[3]}px - 1px)`,
      ...baseSelectedRadiosProminentColourScheme
    },
    'input[type="radio"]:focus + &.standardProminentColourScheme': { // Need to keep this last class because of Styled Components and static classes
      ...baseFocusRadios
    },
    p: 3,
    mt: '2px',
    mb: 2,
    flexGrow: '1'
  },

  experimentalDoubleColumn: {
    ...baseRadios,
    ...textStyles.body.regular.regular,
    borderRadius: '4px',
    'input[type="radio"]:checked + &.experimentalDoubleColumn': { // Need to keep this last class because of Styled Components and static classes
      p: `calc(${space[3]}px - 1px)`,
      ...baseSelectedRadios
    },
    'input[type="radio"]:focus + &.experimentalDoubleColumn': { // Need to keep this last class because of Styled Components and static classes
      ...baseFocusRadios
    },
    p: 3,
    mt: '2px',
    mb: 2,
    flexBasis: 'calc(50% - 34px)',
    display: 'grid',
    alignItems: 'center'
  },
  experimentalDoubleColumnProminentColourScheme: {
    ...baseRadiosProminentColourScheme,
    ...textStyles.body.regular.regular,
    borderRadius: '4px',
    'input[type="radio"]:checked + &.experimentalDoubleColumnProminentColourScheme': { // Need to keep this last class because of Styled Components and static classes
      p: `calc(${space[3]}px - 1px)`,
      ...baseSelectedRadiosProminentColourScheme
    },
    'input[type="radio"]:focus + &.experimentalDoubleColumnProminentColourScheme': { // Need to keep this last class because of Styled Components and static classes
      ...baseFocusRadios
    },
    p: 3,
    mt: '2px',
    mb: 2,
    flexBasis: 'calc(50% - 34px)',
    display: 'grid',
    alignItems: 'center'
  },

  experimentalDoubleColumnSmall: {
    ...textStyles.body.regular.small,
    border: 'solid 1px #e0e0e0',
    p: 2,
    mt: '2px',
    mb: 2,
    flexBasis: 'calc(50% - 24px)',
    '@media screen and (max-width: 600px)': {
      'input[type="radio"]:focus + &.experimentalDoubleColumnSmall': {
        borderColor: '#EAEEFA'
      }
    },
    display: 'grid',
    alignItems: 'center',
    textAlign: 'center!important',
    cursor: 'pointer',
    color: 'rgba(51,51,51,0.87)',
    borderRadius: '8px',
    '@media (hover: hover)': { // Only apply these styles if the browser supports proper/true/real/non-emulated hovering (e.g. has a mouse-like primary input device)
      '&:hover': {
        borderColor: '#dde3f8',
        backgroundColor: '#dde3f8'
      }
    },
    'input[type="radio"]:checked + &.experimentalDoubleColumnSmall': { // Need to keep this last class because of Styled Components and static classes
      color: '#1a56f1',
      borderColor: '#EAEEFA',
      backgroundColor: '#EAEEFA'
    },
    'input[type="radio"]:focus + &.experimentalDoubleColumnSmall': { // Need to keep this last class because of Styled Components and static classes
      color: '#1a56f1',
      backgroundColor: '#EAEEFA',
      borderColor: '#2e51d3'
    }
  },
  experimentalDoubleColumnSmallProminentColourScheme: {
    ...textStyles.prominentColourScheme.body.small,
    backgroundColor: '#fff',
    border: `solid 1px ${muiColors.prominent.lightGrey()}`,
    height: '50px',
    flexBasis: 'calc(50% - 6.5px)',
    boxSizing: 'border-box',
    display: 'grid',
    alignItems: 'center',
    textAlign: 'center!important',
    cursor: 'pointer',
    color: 'rgba(51,51,51,0.87)',
    borderRadius: '8px',
    '@media (hover: hover)': { // Only apply these styles if the browser supports proper/true/real/non-emulated hovering (e.g. has a mouse-like primary input device)
      '&:hover': {
        color: muiColors.prominent.darkGrey(),
        border: 'none',
        backgroundColor: muiColors.prominent.blue(0.09)
      }
    },
    'input[type="radio"]:checked + &.experimentalDoubleColumnSmallProminentColourScheme': { // Need to keep this last class because of Styled Components and static classes
      color: muiColors.prominent.blue(),
      border: `2px solid ${muiColors.prominent.blue()}`,
      backgroundColor: muiColors.prominent.blue(0.09),
      boxSizing: 'border-box'
    },
    'input[type="radio"]:focus + &.experimentalDoubleColumnSmallProminentColourScheme': { // Need to keep this last class because of Styled Components and static classes
      color: '#1a56f1',
      backgroundColor: '#EAEEFA',
      borderColor: '#2e51d3'
    }
  },

  experimentalDoubleColumnSmallHorizontal: {
    ...textStyles.body.regular.small,
    border: '1px solid #e0e0e0',
    p: 2,
    mt: '2px',
    mb: 2,
    flexBasis: 'calc(50% - 24px)',
    cursor: 'pointer',
    color: 'rgba(51,51,51,0.87)',
    borderRadius: '8px',
    '@media (hover: hover)': { // Only apply these styles if the browser supports proper/true/real/non-emulated hovering (e.g. has a mouse-like primary input device)
      '&:hover': {
        borderColor: '#dde3f8',
        backgroundColor: '#dde3f8'
      }
    },
    'input[type="radio"]:checked + &.experimentalDoubleColumnSmallHorizontal': { // Need to keep this last class because of Styled Components and static classes
      borderColor: '#EAEEFA',
      color: '#1a56f1',
      backgroundColor: '#EAEEFA'
    },
    'input[type="radio"]:focus + &.experimentalDoubleColumnSmallHorizontal': { // Need to keep this last class because of Styled Components and static classes
      borderColor: '#2e51d3',
      color: '#1a56f1',
      backgroundColor: '#EAEEFA'
    },
    display: 'grid',
    alignItems: 'center',
    textAlign: 'center!important',
    '@media screen and (max-width: 600px)': {
      'input[type="radio"]:focus + &.experimentalDoubleColumnSmallHorizontal': {
        borderColor: '#EAEEFA'
      }
    }
  },
  experimentalDoubleColumnSmallHorizontalProminentColourScheme: {
    ...textStyles.prominentColourScheme.body.small,
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    height: '50px',
    flexBasis: 'calc(50% - 2.5px)',
    mt: '12px',
    mb: '30px',
    boxSizing: 'border-box',
    outline: 'unset',
    cursor: 'pointer',
    color: 'rgba(51,51,51,0.87)',
    borderRadius: '8px',
    '@media (hover: hover)': { // Only apply these styles if the browser supports proper/true/real/non-emulated hovering (e.g. has a mouse-like primary input device)
      '&:hover': {
        color: 'rgb(51, 51, 51)',
        outline: 'none',
        backgroundColor: 'rgba(31, 46, 209, 0.09)'
      }
    },
    'input[type="radio"]:checked + &.experimentalDoubleColumnSmallHorizontalProminentColourScheme': { // Need to keep this last class because of Styled Components and static classes
      color: 'rgba(31, 46, 207, 1)',
      backgroundColor: 'rgba(31, 46, 209, 0.09)',
      border: '2px solid #1f2ecf',
      boxSizing: 'border-box',
      outline: 'unset'
    },
    'input[type="radio"]:focus + &.experimentalDoubleColumnSmallHorizontalProminentColourScheme': { // Need to keep this last class because of Styled Components and static classes
      borderColor: '#2e51d3',
      color: '#1a56f1',
      backgroundColor: '#EAEEFA'
    },
    display: 'grid',
    alignItems: 'center',
    textAlign: 'center!important'
  }
}

export default radios
